<ng-container *vsLet="payment$ | async as payment">
  <vs-dialog
    (closed)="paymentForm.reset(); isEditing = false"
    #dialog
    [config]="{ dialogContainerClass: 'w-[500px] max-w-[800px]' }"
  >
    <vs-dialog-header
      [config]="{
      mode: 'simple',
      showDivider: true,
      
      icon: 'bank-note-01',
      iconTheme: 'primary',
      
      title: payment ? 'Edit ' + (payment.title) : 'Create new payment',
      subtitle: payment ? 'Edit the details of ' + (payment.title) + '.' : 'Fill out the details below to create a payment for this claim.'
    }"
    ></vs-dialog-header>

    <ng-container *ngIf="claim$ | async as claim">
      <ng-container *ngIf="lazyLoad$ | async; else isLoading">
        <form class="flex flex-col gap-4 p-4" [formGroup]="paymentForm">
          <!-- Claims that need authorization are paid one share of the split at a time. -->
          <vs-form-field *ngIf="shares" [config]="{ label: 'Pay', labelClass: '!w-[105px]'}" appearance="horizontal">
            <vs-select
              class="flex-grow"
              vsInput
              [value]="share?.allocationId ?? null"
              [config]="{ placeholder: 'Choose a share of the split...' }"
              (valueChanges)="selectShare($any($event))"
            >
              <li *ngFor="let allocation of shares" vs-select-option [value]="allocation.id">
                {{ describePayee(allocation) }} · {{ formatAmount(allocation.amount) }}<ng-container
                  *ngIf="references?.[allocation.authorizationId] as reference"
                >
                  · {{ reference }}</ng-container
                >
              </li>
            </vs-select>
            <div *ngIf="heldBackHint" slot="hint">{{ heldBackHint }}</div>
          </vs-form-field>

          <p *ngIf="!shares && getPayee(claim, payment) as payee" class="text-sm text-gray-600">
            Payee: <span class="font-medium text-gray-900">{{ payee.label }}</span>
          </p>

          <vs-form-field
            [config]="{ label: 'Title', labelClass: '!w-[105px]'}"
            [theme]="paymentForm.controls.title.touched && paymentForm.controls.title.invalid ? 'danger' : 'primary'"
            appearance="horizontal"
          >
            <input vsInput type="text" formControlName="title" placeholder="Enter payment title" class="w-full" />
            <div
              *ngIf="paymentForm.controls.title.touched && paymentForm.controls.title.invalid"
              class="text-danger-600"
              slot="hint"
            >
              Title is required and max # of characters is 255
            </div>
          </vs-form-field>

          <vs-form-field
            [config]="{ label: 'Description', labelClass: '!w-[105px]'}"
            [theme]="paymentForm.controls.description.touched && paymentForm.controls.description.invalid ? 'danger' : 'primary'"
            appearance="horizontal"
          >
            <textarea
              vsInput
              min="2"
              max="5"
              type="text"
              formControlName="description"
              placeholder="Describe payment details"
            ></textarea>

            <div
              *ngIf="paymentForm.controls.description.touched && paymentForm.controls.description.invalid"
              class="text-danger-600"
              slot="hint"
            >
              Description is required
            </div>
          </vs-form-field>

          <vs-form-field
            *ngIf="payment && payment.status === paymentStatus.DRAFT"
            [config]="{ label: 'Status', labelClass: '!w-[105px]'}"
            appearance="horizontal"
          >
            <vs-select
              class="flex-grow"
              vsInput
              formControlName="status"
              [config]="{ placeholder: 'Select a status...' }"
            >
              <li vs-select-option [value]="paymentStatus.DRAFT">Draft</li>
              <li vs-select-option [value]="paymentStatus.COMPLETED">Completed</li>
            </vs-select>
          </vs-form-field>

          <!-- Shops can't receive bank transfers yet: their payments are marked as paid instead. -->
          <p *ngIf="getPayee(claim, payment)?.isShop; else destinationField" class="text-sm text-gray-500">
            Shop payments can't be sent through Dwolla yet. Mark the payment as paid once the shop is paid.
          </p>
          <ng-template #destinationField>
            <vs-form-field
              [config]="{ label: 'Destination', labelClass: '!w-[105px]'}"
              appearance="horizontal"
              *vsLet="destinations$ | async as destinations"
            >
              <vs-claim-payment-method-picker
                class="flex-grow"
                [destinations]="destinations"
                [customer]="customer"
                [customerId]="customerId"
                [canAddPaymentMethod]="canManagePaymentMethods"
                formControlName="destination"
                (refresh)="refreshDestinations()"
              ></vs-claim-payment-method-picker>
            </vs-form-field>
          </ng-template>

          <vs-form-field
            [config]="{ label: 'Amount', labelClass: '!w-[105px]'}"
            [theme]="paymentForm.controls.amount.touched && paymentForm.controls.amount.invalid ? 'danger' : 'primary'"
            appearance="horizontal"
          >
            <input
              vsInput
              type="text"
              formControlName="amount"
              placeholder="Enter payment amount"
              currencyMask
              [options]="currencyMaskConfig"
              [readonly]="isAmountLocked(payment)"
            />
            <div
              class="text-danger-600"
              *ngIf="paymentForm.controls.amount.touched && paymentForm.controls.amount.invalid"
              slot="hint"
            >
              Amount is required
            </div>
            <div *ngIf="isAmountLocked(payment)" slot="hint">
              The share of the split. Change the split to change it.
            </div>
            <div
              *ngIf="!isAmountLocked(payment) && getBalance(claim, payment) as balance"
              slot="hint"
              [class.text-danger-600]="isOverRemaining(claim, payment)"
            >
              {{ isOverRemaining(claim, payment) ? 'More than the' : 'Up to' }} {{ formatAmount(getRemaining(claim,
              payment) ?? 0) }} left on authorization {{ balance.authorizationReference }}
            </div>
          </vs-form-field>

          <vs-form-field [config]="{ label: 'Links (optional)', labelClass: '!w-[105px]'}" appearance="horizontal">
            <ws-claim-link-picker
              [claim]="claim"
              [id]="payment?.id ?? null"
              placeholder="Add links to this payment"
              class="flex flex-row gap-4 justify-center w-full"
              formControlName="links"
            ></ws-claim-link-picker>
          </vs-form-field>
        </form>

        <div class="flex gap-2 justify-end mt-4">
          <button vs-button appearance="outline" (click)="close()">Cancel</button>
          <button
            vs-button
            theme="primary"
            [disabled]="!paymentForm.valid || isOverRemaining(claim, payment) || (!!shares && !share)"
            (click)="save(claim, isEditing, payment?.id)"
          >
            Save
          </button>
        </div>
      </ng-container>
      <ng-template #isLoading>
        <div class="flex items-center justify-center min-h-[350px]">
          <vs-icon name="loading-01" class="animate-spin text-primary-500" size="40"></vs-icon>
        </div>
      </ng-template>
    </ng-container>
  </vs-dialog>
</ng-container>
